import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

const POINTS = [
  "Stronger hooks",
  "Viral content",
  "Higher retention",
  "Better structure",
  "Grow audience",
  "Stronger reach",
  "High-CTR thumbnails",
  "Save time",
];

/* Views curve: flat near-zero for ~70% of the timeline, then a dramatic
   smooth spike that levels slightly at the top. pathLength="1" normalizes
   the stroke so CSS can draw it with a single dashoffset transition. */
const LINE_D =
  "M40,326 C80,324 100,329 130,327 C160,325 185,330 215,328 C245,326 265,329 295,327 C325,325 345,329 375,328 C405,327 425,329 455,327 C485,325 505,327 520,324 C560,314 602,238 640,118 C662,66 694,50 730,44";

const FILL_D =
  "M508,325 C548,317 602,238 640,118 C662,66 694,50 730,44 L730,336 L508,336 Z";

export const BreakthroughSection = () => {
  const cardRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="breakthrough"
      className="lazy-section pt-16 pb-[120px] px-6 sm:px-10"
      data-testid="breakthrough-section"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-x-16 gap-y-10 items-center">
        {/* ---------------- Graph card (left on desktop, after text on mobile) ---------------- */}
        <div className="order-2 lg:order-1 lg:row-span-2">
          <div
            ref={cardRef}
            className={`breakthrough-card squircle-lg border border-[#2b2b2b] bg-[#0d0d0d] p-6 sm:p-8 ${
              inView ? "is-inview" : ""
            }`}
            data-testid="breakthrough-chart"
          >
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div data-testid="breakthrough-views-metric">
              <div className={`text-4xl sm:text-5xl font-bold tracking-tighter tabular-nums bk-metric ${inView ? "is-inview" : ""}`}>
                1,422,616
              </div>
              <div className="mt-1 text-xs uppercase tracking-widest text-[#a0a0a0]">
                Views · last 30 days
              </div>
            </div>
            <div className="text-left sm:text-right text-sm leading-relaxed">
              <div className="text-[#e5e5e5]">
                <span className="font-semibold">2.4%</span>{" "}
                <span className="text-[#a0a0a0]">followers</span>
              </div>
              <div className="text-[#e5e5e5]">
                <span className="font-semibold">97.6%</span>{" "}
                <span className="text-[#a0a0a0]">non-followers</span>
              </div>
            </div>
          </div>

          <svg
            viewBox="0 0 760 380"
            className="w-full h-auto block"
            role="img"
            aria-label="Views chart: near-zero for weeks, then a dramatic spike to 1.3 million views"
          >
            <defs>
              <linearGradient id="bk-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--bk-line)" stopOpacity="0.28" />
                <stop offset="100%" stopColor="var(--bk-line)" stopOpacity="0" />
              </linearGradient>
              <filter id="bk-glow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="8" />
              </filter>
            </defs>

            {/* gridlines */}
            {[44, 185, 326].map((y) => (
              <line key={y} x1="40" y1={y} x2="730" y2={y} stroke="var(--bk-grid)" strokeWidth="1" />
            ))}

            {/* y labels */}
            <text x="0" y="48" className="bk-axis">1.3M</text>
            <text x="0" y="189" className="bk-axis">690K</text>
            <text x="0" y="330" className="bk-axis">0</text>

            {/* x labels */}
            <text x="40" y="364" className="bk-axis">May 26</text>
            <text x="270" y="364" textAnchor="middle" className="bk-axis bk-axis--mid">Jun 6</text>
            <text x="520" y="364" textAnchor="middle" className="bk-axis">Jun 16</text>
            <text x="730" y="364" textAnchor="end" className="bk-axis">Jun 24</text>

            {/* gradient under the breakout portion */}
            <path d={FILL_D} fill="url(#bk-fill)" className="bk-fill" />

            {/* glow behind the spike */}
            <path
              d={LINE_D}
              pathLength="1"
              fill="none"
              stroke="var(--bk-line)"
              strokeWidth="10"
              strokeLinecap="round"
              filter="url(#bk-glow)"
              className="bk-line bk-line--glow"
            />

            {/* primary views curve */}
            <path
              d={LINE_D}
              pathLength="1"
              fill="none"
              stroke="var(--bk-line)"
              strokeWidth="3"
              strokeLinecap="round"
              className="bk-line"
            />

            {/* key data points */}
            <circle cx="520" cy="324" r="5" className="bk-dot" style={{ transitionDelay: "1.9s" }} />
            <circle cx="730" cy="44" r="6" className="bk-dot" style={{ transitionDelay: "2.5s" }} />
          </svg>
          </div>
          <p
            className="mt-3 text-xs text-[#7a7a7a]"
            data-testid="breakthrough-disclaimer"
          >
            Illustrative example — results may vary.
          </p>
        </div>

        {/* ---------------- Copy (right on desktop, first on mobile) ---------------- */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="order-1 lg:order-2 lg:self-end"
        >
          <div className="text-xs uppercase tracking-widest text-[#a0a0a0] mb-4">
            The breakthrough
          </div>
          <h2
            className="text-4xl sm:text-5xl font-bold tracking-tighter leading-[1.05]"
            data-testid="breakthrough-headline"
          >
            Stop pouring energy into content that never gets seen.
          </h2>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
          className="order-3 lg:self-start grid grid-cols-2 gap-x-6 gap-y-3"
          data-testid="breakthrough-points"
        >
          {POINTS.map((point) => (
            <li key={point} className="flex items-center gap-3 text-[#e5e5e5]">
              <Check size={15} className="bk-check shrink-0" />
              <span className="text-sm sm:text-base">{point}</span>
            </li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
};

export default BreakthroughSection;
